var __decorate = (this && this.__decorate) || function (decorators, target, key, desc) {
    var c = arguments.length, r = c < 3 ? target : desc === null ? desc = Object.getOwnPropertyDescriptor(target, key) : desc, d;
    if (typeof Reflect === "object" && typeof Reflect.decorate === "function") r = Reflect.decorate(decorators, target, key, desc);
    else for (var i = decorators.length - 1; i >= 0; i--) if (d = decorators[i]) r = (c < 3 ? d(r) : c > 3 ? d(target, key, r) : d(target, key)) || r;
    return c > 3 && r && Object.defineProperty(target, key, r), r;
};
import { html, KompisElement, property } from '@inter-ikea-kompis/web-component';
import { InvalidProductReasonEnum } from '@inter-ikea-kompis/enums';
import { Checkbox } from '@inter-ikea-kompis/component-checkbox';
import { Input } from '@inter-ikea-kompis/component-input';
import { Text } from '@inter-ikea-kompis/component-text';
import { Button as SkapaButton, IconButton as SkapaIconButton } from '@ingka/button-webc/passive.js';
import FakeDataStyle from './FakeDataStyle.js';
/**
 * Fake Data component.
 *
 * @event applyChanges Emitted when applying the product data changes
 */
export default class FakeData extends KompisElement {
    constructor() {
        super(...arguments);
        // List of products.
        this.products = null;
        this.showError = false;
        this.useFamilyPrice = false;
        this.useTROPrice = false;
        this.isOnlineSellable = true;
        this.productIdsInput = null;
        this.fromDateInput = null;
        this.toDateInput = null;
        this.specialPriceInput = null;
        this.regularPriceInput = null;
        this.changedProducts = [];
        this.selectedProduct = null;
        this.changedProductsText = '';
        this.dateInputRegex = /^[0-9)(-]+$/;
        this.priceInputRegex = /^[0-9)(.]+$/;
    }
    /**
     * @returns Template.
     */
    render() {
        return html `
			<style>
				${FakeDataStyle.getStyle({
            theme: this.theme
        })}
			</style>
			<div class="container">
				${this.getSearch()} ${this.getInvalidateButton()} ${this.getCheckboxes()}
				${this.getDateInputs()} ${this.getPriceInputs()} ${this.getApplyButton()}
				${this.getChangedProductsText()} ${this.getLoadButton()}
			</div>
		`;
    }
    async connectedCallback() {
        this.customElements.define('skapa-button', SkapaButton);
        this.customElements.define('skapa-icon-button', SkapaIconButton);
        this.customElements.define('kompis-text', Text);
        this.customElements.define('kompis-input', Input);
        this.customElements.define('kompis-checkbox', Checkbox);
        super.connectedCallback();
    }
    /**
     * Get HTML for search input and button.
     *
     * @returns Template.
     */
    getSearch() {
        const emphasisedVariant = 'emphasised';
        return html `
			<div class="searchWrapper">
				<kompis-input
					.theme="${this.theme}"
					.label="${'Product id'}"
					.value="${this.productIdsInput}"
					.errorMessage="${'ID not found'}"
					.invalid="${this.showError}"
					.inputMode="${'numeric'}"
					@input="${this.onInput.bind(this)}"
					@focusin="${this.onInputFocus.bind(this)}"
				></kompis-input>
				<skapa-button
					class="getProductButton"
					variant="${emphasisedVariant}"
					?disabled="${!this.productIdsInput}"
					@click="${this.onGetProductButtonClick.bind(this)}"
				>
					Get product
				</skapa-button>
			</div>
		`;
    }
    /**
     * Get HTML for invalidate button.
     *
     * @returns Template.
     */
    getInvalidateButton() {
        const emphasisedVariant = 'emphasised';
        return html `
			<skapa-button
				class="invalidateButton"
				variant="${emphasisedVariant}"
				?disabled="${!this.selectedProduct}"
				@click="${this.onInvalidateProductButtonClick.bind(this)}"
			>
				Invalidate product
			</skapa-button>
		`;
    }
    /**
     * Get HTML for checkboxes.
     *
     * @returns Template.
     */
    getCheckboxes() {
        return html `
			<div class="checkboxes">
				<kompis-checkbox
					.theme="${this.theme}"
					.label="${'Family price'}"
					.checked="${this.useFamilyPrice}"
					.disabled="${!this.selectedProduct}"
					@change="${this.onChangeFamilyPrice.bind(this)}"
				></kompis-checkbox>
				<kompis-checkbox
					.theme="${this.theme}"
					.label="${'Time Restricted Offer'}"
					.checked="${this.useTROPrice}"
					.disabled="${!this.selectedProduct}"
					@change="${this.onChangeTROPrice.bind(this)}"
				></kompis-checkbox>
				<kompis-checkbox
					.theme="${this.theme}"
					.label="${'Online sellable'}"
					.checked="${this.isOnlineSellable}"
					.disabled="${!this.selectedProduct}"
					@change="${this.onChangeIsOnlineSellable.bind(this)}"
				></kompis-checkbox>
			</div>
		`;
    }
    /**
     * Get HTML for date inputs.
     *
     * @returns Template.
     */
    getDateInputs() {
        return html `
			<div class="dateInputs">
				<kompis-input
					.theme="${this.theme}"
					.label="${'Date from'}"
					.disabled="${!this.useTROPrice}"
					.value="${this.fromDateInput}"
					.placeholder="${'yyyy-mm-dd'}"
					@input="${this.onDateFromInput.bind(this)}"
				></kompis-input>
				<kompis-input
					.theme="${this.theme}"
					.label="${'Date to'}"
					.placeholder="${'yyyy-mm-dd'}"
					.disabled="${!this.useTROPrice}"
					.value="${this.toDateInput}"
					@input="${this.onDateToInput.bind(this)}"
				></kompis-input>
			</div>
		`;
    }
    /**
     * Get HTML for price inputs.
     *
     * @returns Template.
     */
    getPriceInputs() {
        return html `
			<div class="priceInputs">
				<kompis-input
					.theme="${this.theme}"
					.label="${'Special price'}"
					.disabled="${!this.useTROPrice && !this.useFamilyPrice}"
					.value="${this.specialPriceInput}"
					@input="${this.onSpecialPriceInput.bind(this)}"
				></kompis-input>
				<kompis-input
					.theme="${this.theme}"
					.label="${'Regular price'}"
					.disabled="${!this.useTROPrice && !this.useFamilyPrice}"
					.value="${this.regularPriceInput}"
					@input="${this.onRegularPriceInput.bind(this)}"
				></kompis-input>
			</div>
		`;
    }
    /**
     * Get HTML for apply changes button.
     *
     * @returns Template.
     */
    getApplyButton() {
        const emphasisedVariant = 'emphasised';
        return html `
			<skapa-button
				class="applyButton"
				variant="${emphasisedVariant}"
				?disabled="${!this.selectedProduct}"
				@click="${this.onApplyChangesButtonClick.bind(this)}"
			>
				Apply changes for product
			</skapa-button>
		`;
    }
    /**
     * Get HTML for changed products text.
     *
     * @returns Template.
     */
    getChangedProductsText() {
        return html `
			<div class="changedProducts">
				<kompis-text .theme="${this.theme}" .bold="${true}">Changed items:</kompis-text>
				<kompis-text .theme="${this.theme}">${this.changedProductsText}</kompis-text>
			</div>
		`;
    }
    /**
     * Get HTML load fake data button.
     *
     * @returns Template.
     */
    getLoadButton() {
        const emphasisedVariant = 'emphasised';
        return html `
			<skapa-button
				class="loadButton"
				variant="${emphasisedVariant}"
				@click="${this.onLoadButtonClick.bind(this)}"
			>
				Load fake data
			</skapa-button>
		`;
    }
    /**
     * Handle input event from the Input component.
     *
     * @param event Event.
     */
    onInput(event) {
        this.productIdsInput = event.detail.value.substring(0, 8);
        this.requestUpdate();
    }
    /**
     * Handle input focus event from the Input component.
     *
     */
    onInputFocus() {
        this.showError = false;
        this.requestUpdate();
    }
    /**
     * Handle date from input event.
     *
     * @param event Event.
     */
    onDateFromInput(event) {
        if (this.dateInputRegex.test(event.detail.value) || event.detail.value === '') {
            this.fromDateInput = event.detail.value.substring(0, 10);
            this.requestUpdate();
        }
    }
    /**
     * Handle date to input event.
     *
     * @param event Event.
     */
    onDateToInput(event) {
        if (this.dateInputRegex.test(event.detail.value) || event.detail.value === '') {
            this.toDateInput = event.detail.value.substring(0, 10);
            this.requestUpdate();
        }
    }
    /**
     * Handle special price input event.
     *
     * @param event Event.
     */
    onSpecialPriceInput(event) {
        if (this.priceInputRegex.test(event.detail.value) || event.detail.value === '') {
            this.specialPriceInput = event.detail.value;
            this.requestUpdate();
        }
    }
    /**
     * Handle regular price input event.
     *
     * @param event Event.
     */
    onRegularPriceInput(event) {
        if (this.priceInputRegex.test(event.detail.value) || event.detail.value === '') {
            this.regularPriceInput = event.detail.value;
            this.requestUpdate();
        }
    }
    /**
     * Handle change family price checkbox value.
     *
     */
    onChangeFamilyPrice() {
        this.useFamilyPrice = !this.useFamilyPrice;
        this.requestUpdate();
    }
    /**
     * Handle change family price checkbox value.
     *
     */
    onChangeTROPrice() {
        this.useTROPrice = !this.useTROPrice;
        this.requestUpdate();
    }
    /**
     * Handle change online sellable checkbox value.
     *
     */
    onChangeIsOnlineSellable() {
        this.isOnlineSellable = !this.isOnlineSellable;
        this.requestUpdate();
    }
    /**
     * Handle get product button click.
     */
    onGetProductButtonClick() {
        const productItem = this.products.find((product) => {
            return (product.content &&
                (product.content.itemNoGlobal === this.productIdsInput ||
                    product.content.itemNoLocal === this.productIdsInput));
        });
        if (productItem) {
            this.selectedProduct = productItem;
        }
        else {
            this.showError = true;
        }
        this.requestUpdate();
    }
    /**
     * Handle invalidate product button click.
     */
    onInvalidateProductButtonClick() {
        const changedProduct = this.selectedProduct;
        changedProduct.valid = false;
        changedProduct.reason = [
            {
                code: InvalidProductReasonEnum.retailItemNotAvailable,
                message: 'This item is not available'
            }
        ];
        this.changedProducts.push(changedProduct);
        this.setChangedProductsText();
        this.resetValues();
        this.requestUpdate();
    }
    /**
     * Handle apply changes button click.
     */
    onApplyChangesButtonClick() {
        let changedProduct = this.selectedProduct;
        if (changedProduct?.content) {
            changedProduct.content.priceInformation.availableForClickCollect = this.isOnlineSellable;
            changedProduct.content.priceInformation.availableForHomeDelivery = this.isOnlineSellable;
            if (this.useFamilyPrice && this.specialPriceInput !== '') {
                changedProduct = this.addFamilyPrice(changedProduct);
            }
            else if (this.useTROPrice &&
                this.fromDateInput &&
                this.toDateInput &&
                this.specialPriceInput !== '') {
                changedProduct = this.addTROPrice(changedProduct);
            }
            this.changedProducts.push(changedProduct);
            this.setChangedProductsText();
            this.resetValues();
            this.requestUpdate();
        }
    }
    /**
     * Adds family price to product.
     *
     */
    setChangedProductsText() {
        this.changedProductsText = '';
        this.changedProducts.forEach((changedProduct, index) => {
            if (changedProduct.content) {
                const productItemNo = changedProduct.content.itemNoGlobal || changedProduct.content.itemNoLocal;
                this.changedProductsText += productItemNo;
                if (index !== this.changedProducts.length - 1) {
                    this.changedProductsText += ', ';
                }
            }
        });
    }
    /**
     * Adds family price to product.
     *
     * @param changedProduct Product to add family price for.
     * @returns The changed product with family price.
     */
    addFamilyPrice(changedProduct) {
        let changedPriceWithFamilyPrice = changedProduct;
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        if (!changedProduct.content.priceInformation.familyPrice) {
            const shouldUseTRO = this.useTROPrice && this.fromDateInput && this.toDateInput ? true : false;
            const familyPrice = {
                // eslint-disable-next-line @typescript-eslint/no-explicit-any
                currencyCode: changedProduct.content.priceInformation.price
                    ? // eslint-disable-next-line @typescript-eslint/no-explicit-any
                        changedProduct.content.priceInformation.price.currencyCode
                    : changedProduct.content.priceInformation.salesPrice[0].currencyCode,
                priceExclTax: Number(this.specialPriceInput) - 2,
                priceInclTax: Number(this.specialPriceInput),
                indicativeTotalTax: Number(this.specialPriceInput) + 2,
                previousSalesPrice: {
                    priceInclTax: Number(this.specialPriceInput),
                    priceExclTax: Number(this.specialPriceInput) - 2,
                    indicativeTotalTax: Number(this.specialPriceInput) + 2
                },
                typeCode: 'IKEAFamilySalesUnitPrice',
                validFromText: this.fromDateInput || null,
                validToText: this.toDateInput || null,
                changeReason: shouldUseTRO ? 'Temporary' : 'family',
                validToDateTime: null,
                validFromDateTime: null
            };
            // eslint-disable-next-line @typescript-eslint/no-explicit-any
            changedPriceWithFamilyPrice.content.priceInformation.familyPrice = familyPrice;
            changedPriceWithFamilyPrice.content.priceInformation.salesPrice.push(familyPrice);
            if (shouldUseTRO) {
                // eslint-disable-next-line @typescript-eslint/no-explicit-any
                changedPriceWithFamilyPrice.content.priceInformation.temporaryFamilyPrice = true;
            }
        }
        else {
            // eslint-disable-next-line @typescript-eslint/no-explicit-any
            changedPriceWithFamilyPrice.content.priceInformation.familyPrice.priceInclTax =
                Number(this.specialPriceInput);
            const familySalesPriceIndex = changedProduct.content.priceInformation.salesPrice.indexOf(changedProduct.content.priceInformation.salesPrice.find((priceObject) => {
                return priceObject.typeCode === 'IKEAFamilySalesUnitPrice';
            }));
            changedPriceWithFamilyPrice.content.priceInformation.salesPrice[familySalesPriceIndex].priceInclTax = Number(this.specialPriceInput);
        }
        changedPriceWithFamilyPrice = this.addRegularPrice(changedProduct);
        return changedPriceWithFamilyPrice;
    }
    /**
     * Adds TRO (Time Restricted Offer) price to product.
     *
     * @param changedProduct Product to add TRO price for.
     * @returns The changed product with TRO price.
     */
    addTROPrice(changedProduct) {
        let changedPriceWithTROPrice = changedProduct;
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        changedPriceWithTROPrice.content.priceInformation.timeRestrictedOffer = true;
        const temporarySalesPriceIndex = changedProduct.content.priceInformation.salesPrice.indexOf(changedProduct.content.priceInformation.salesPrice.find((priceObject) => {
            return priceObject.typeCode === 'TimeRestrictedOffer';
        }));
        const temporaryPrice = {
            // eslint-disable-next-line @typescript-eslint/no-explicit-any
            currencyCode: changedProduct.content.priceInformation.price
                ? // eslint-disable-next-line @typescript-eslint/no-explicit-any
                    changedProduct.content.priceInformation.price.currencyCode
                : changedProduct.content.priceInformation.salesPrice[0].currencyCode,
            priceExclTax: Number(this.specialPriceInput) - 2,
            priceInclTax: Number(this.specialPriceInput),
            indicativeTotalTax: Number(this.specialPriceInput) + 2,
            previousSalesPrice: {
                priceInclTax: Number(this.regularPriceInput),
                priceExclTax: Number(this.regularPriceInput) - 2,
                indicativeTotalTax: Number(this.regularPriceInput) + 2
            },
            typeCode: 'TimeRestrictedOffer',
            validFromText: this.fromDateInput,
            validToText: this.toDateInput,
            changeReason: 'TimeRestrictedOffer',
            validToDateTime: null,
            validFromDateTime: null
        };
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        changedPriceWithTROPrice.content.priceInformation.price = temporaryPrice;
        if (temporarySalesPriceIndex > -1) {
            changedPriceWithTROPrice.content.priceInformation.salesPrice[temporarySalesPriceIndex] =
                temporaryPrice;
        }
        else {
            changedPriceWithTROPrice.content.priceInformation.salesPrice.push(temporaryPrice);
        }
        changedPriceWithTROPrice = this.addRegularPrice(changedProduct);
        return changedPriceWithTROPrice;
    }
    /**
     * Adds regular price to product.
     *
     * @param changedProduct Product to add regular price for.
     * @returns The changed product with regular price.
     */
    addRegularPrice(changedProduct) {
        const changedPriceWithRegularPrice = changedProduct;
        if (this.regularPriceInput !== '') {
            const regularPrice = {
                typeCode: 'RegularSalesUnitPrice',
                priceExclTax: Number(this.regularPriceInput) - 2,
                priceInclTax: Number(this.regularPriceInput),
                indicativeTotalTax: Number(this.specialPriceInput) + 2,
                previousSalesPrice: {
                    priceInclTax: Number(this.specialPriceInput),
                    priceExclTax: Number(this.specialPriceInput) - 2,
                    indicativeTotalTax: Number(this.specialPriceInput) + 2
                },
                changeReason: 'regular',
                validToDateTime: null,
                validFromDateTime: null,
                validFromText: null,
                validToText: null,
                // eslint-disable-next-line @typescript-eslint/no-explicit-any
                currencyCode: changedProduct.content.priceInformation.price
                    ? // eslint-disable-next-line @typescript-eslint/no-explicit-any
                        changedProduct.content.priceInformation.price.currencyCode
                    : changedProduct.content.priceInformation.salesPrice[0].currencyCode
            };
            const regularSalesPriceIndex = changedProduct.content.priceInformation.salesPrice.indexOf(changedProduct.content.priceInformation.salesPrice.find((priceObject) => {
                return priceObject.typeCode === 'RegularSalesUnitPrice';
            }));
            // eslint-disable-next-line @typescript-eslint/no-explicit-any
            changedPriceWithRegularPrice.content.priceInformation.regularPrice = regularPrice;
            changedPriceWithRegularPrice.content.priceInformation.salesPrice[regularSalesPriceIndex] =
                regularPrice;
            if (
            // eslint-disable-next-line @typescript-eslint/no-explicit-any
            changedProduct.content.priceInformation.price &&
                // eslint-disable-next-line @typescript-eslint/no-explicit-any
                changedProduct.content.priceInformation.price.typeCode === 'RegularSalesUnitPrice') {
                // eslint-disable-next-line @typescript-eslint/no-explicit-any
                changedPriceWithRegularPrice.content.priceInformation.price = regularPrice;
            }
        }
        return changedPriceWithRegularPrice;
    }
    /**
     * Handle load fake data button click.
     */
    onLoadButtonClick() {
        const updatedProducts = this.products.map((product) => {
            if (this.changedProducts.includes(product)) {
                const index = this.changedProducts.indexOf(product);
                return this.changedProducts[index];
            }
            return product;
        });
        this.dispatchEvent(new CustomEvent('applyChanges', {
            bubbles: true,
            detail: {
                updatedProducts: updatedProducts
            }
        }));
    }
    /**
     * Resets all values set for a product.
     */
    resetValues() {
        this.selectedProduct = null;
        this.isOnlineSellable = true;
        this.useFamilyPrice = false;
        this.useTROPrice = false;
        this.productIdsInput = '';
        this.specialPriceInput = '';
        this.regularPriceInput = '';
        this.fromDateInput = '';
        this.toDateInput = '';
    }
}
__decorate([
    property({ type: Object, required: true })
], FakeData.prototype, "theme", void 0);
__decorate([
    property({ type: Object })
], FakeData.prototype, "products", void 0);
//# sourceMappingURL=FakeData.js.map